import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getDatabase, connectDatabaseEmulator } from 'firebase/database';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { PLATFORM_HOSTNAMES } from './platformHostnames';

// Credenziali da variabili d'ambiente (.env)
//
// ⚠️  authDomain — deve puntare SEMPRE al dominio Firebase del progetto
// (es. noleggioincloud.firebaseapp.com), MAI al dominio personalizzato del
// cliente su cui gira l'app.
//
// PERCHÉ: signInWithRedirect usa authDomain come destinazione intermedia del
// flusso OAuth di Google. Firebase Auth accetta solo i domini nella whitelist
// "Authorized domains" della Console — i domini personalizzati dei clienti
// (es. tuonoleggio.it) non ci sono e non ci possono stare in modo scalabile.
// Fissando authDomain al dominio Firebase il redirect OAuth passa sempre per
// un dominio autorizzato, indipendentemente da quale hostname sta visitando il
// cliente. Dopo il login Google, il browser ritorna all'URL originale del
// cliente e getRedirectResult() restituisce l'utente autenticato.
//
// In .env: REACT_APP_FIREBASE_AUTH_DOMAIN=noleggioincloud.firebaseapp.com
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN, // noleggioincloud.firebaseapp.com — non toccare
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL:       process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

export const app      = initializeApp(firebaseConfig);
export const auth     = getAuth(app);
export const database = getDatabase(app);
export const storage  = getStorage(app);   // Cloud Storage riattivato
// Regione europe-west1: deve coincidere con quella usata in functions/index.js
// (onCall({ region: 'europe-west1' }, ...)), altrimenti le chiamate falliscono.
export const functions = getFunctions(app, 'europe-west1');

// Firebase App Check — protegge le Cloud Function pubbliche (registerCompany,
// checkSlugAvailability) da chiamate automatizzate/bot che bypassano
// l'interfaccia utente. Gratuito, usa reCAPTCHA v3 (invisibile, nessuna sfida
// per l'utente reale). Attivo SOLO se la site key è configurata — se manca,
// l'app funziona comunque (le CF con enforceAppCheck: true rifiuteranno le
// chiamate senza token valido, quindi la site key VA impostata prima del
// deploy in produzione: vedi REACT_APP_RECAPTCHA_SITE_KEY nel .env).
//
// Setup richiesto in Firebase Console (una tantum, gratuito):
//   1. Firebase Console → App Check → registra l'app web → provider reCAPTCHA v3
//   2. Copia la site key generata in REACT_APP_RECAPTCHA_SITE_KEY (.env)
//   3. Firebase Console → App Check → Cloud Functions → imposta "Enforce"
//      per registerCompany e checkSlugAvailability (o tutte, a scelta)
//
// IMPORTANTE — solo sui domini di piattaforma: la site key reCAPTCHA è
// registrata su Google con un elenco fisso di domini autorizzati, e i domini
// personalizzati dei clienti (es. tuonoleggio.it) non ci sono e non possono
// starci uno per uno (sono infiniti e cambiano nel tempo). Le uniche funzioni
// protette da App Check (registerCompany, checkSlugAvailability) vengono
// chiamate SOLO dalla pagina di registrazione e dal pannello admin, che
// vivono esclusivamente sui domini di piattaforma (vedi PLATFORM_HOSTNAMES in
// ./platformHostnames, condiviso con App.js). Su un sito cliente in dominio
// personalizzato queste funzioni non vengono mai chiamate, quindi App Check
// lì non serve: inizializzarlo comunque produrrebbe solo l'errore
// "appCheck/recaptcha-error" in console (dominio non in whitelist su Google),
// senza alcun beneficio di sicurezza.
const isPlatformHostname =
  typeof window !== 'undefined' && PLATFORM_HOSTNAMES.has(window.location.hostname);

if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && isPlatformHostname) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
} else if (
  !process.env.REACT_APP_RECAPTCHA_SITE_KEY &&
  isPlatformHostname &&
  process.env.NODE_ENV !== 'development'
) {
  // In produzione, su dominio piattaforma, senza site key configurata: le
  // chiamate a registerCompany e checkSlugAvailability falliranno
  // silenziosamente lato server (App Check le rifiuta). Log di avviso per non
  // scoprirlo solo in produzione.
  console.warn(
    'App Check non configurato (REACT_APP_RECAPTCHA_SITE_KEY mancante). ' +
    'La registrazione nuove aziende potrebbe non funzionare finché non viene impostata.'
  );
}

// Collega agli emulatori SOLO in sviluppo locale, e solo se esplicitamente
// richiesto con REACT_APP_USE_EMULATOR=true nel file .env. Senza questo blocco
// il frontend in locale chiamava sempre la produzione: era il Problema 2.
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATOR === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectDatabaseEmulator(database, '127.0.0.1', 9000);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  console.log('Firebase collegato agli EMULATORI locali');
}
